import React, { useState } from 'react'

import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { message } from 'ag-grid-community/dist/lib/utils/general'
import { format as formatDate } from 'date-fns'
import { Field, Form, Formik } from 'formik'
import _find from 'lodash/find'
import * as Yup from 'yup'

import { setAppraisalOrderModalOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { InputField, NumberField, SelectField } from '@/components/FormikInputs'
import LockChild from '@/components/LockChild/LockChild'
import {
  appraisalOrderPropertyTypeOptions,
  appraisalOrderReportTypeOptions,
} from '@/features/application/applicationSlice'
import { useCurrentApplication } from '@/graphql/services/application'
import {
  useCreateAppraisalOrderMutation,
  useGetAppraisalOrderQuery,
} from '@/services/appraisalOrder'
import { useGetEscalationsQuery } from '@/services/escalations'
import { useGetLoggedUserQuery } from '@/services/user'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'

const schema = Yup.object().shape({
  street_name: Yup.string().required('The address is required'),
  unit_number: Yup.string().nullable(),
  city: Yup.string().required('The city is required'),
  state: Yup.string().required('The state is required'),
  county: Yup.string().required('The county is required'),
  zip: Yup.string().required('The zip Code is required'),
  property_type: Yup.string().required('The property type is required'),
  report_type: Yup.string().required('The reporty type is required'),
  estimated_value: Yup.number().nullable(),
  new_loan_amount: Yup.number().nullable(),
  new_loan_number: Yup.string().required('The new loan number is required'),
  loan_officer_first_name: Yup.string().required('The loan officer first name is required'),
  loan_officer_last_name: Yup.string().required('The loan officer last name is required'),
  loan_officer_email: Yup.string()
    .email('The email is not valid')
    .required('The loan officer email is required'),
  first_name: Yup.string().required('The first name is required'),
  last_name: Yup.string().required('The last name is required'),
  email: Yup.string().email('The email is required').required('The email is required'),
  phone: Yup.string().required('The phone is required'),
  vendor_instructions: Yup.string().matches(
    /^[ a-zA-Z0-9@?$&%_+-=;'":,()]*$/,
    'Only the following characters are allowed: a-z A-Z 0-9 @ ? $ & % _ + - = ; \' " : , ( )'
  ),
})

export const AppraisalOrderModal = () => {
  const [order, setOrder] = useState(null)
  const [error, setError] = useState(false)
  const dispatch = useAppDispatch()
  const appraisalOrderModelOpen = useAppSelector((state) => state.general.appraisalOrderModalOpen)

  const { data: user } = useGetLoggedUserQuery()
  const { data: application } = useCurrentApplication()
  const { data: escalations } = useGetEscalationsQuery(application?.externalId, {
    skip: !application?.externalId,
  })

  // If appraisalOrderModelOpen is a number, we are trying to show a form that was ordered
  const appraisalOrderViewOnly = typeof appraisalOrderModelOpen === 'number'
  const appraisalId: number = appraisalOrderViewOnly ? appraisalOrderModelOpen : null
  const { data: appraisalOrders } = useGetAppraisalOrderQuery(
    { id: appraisalId },
    {
      skip: !appraisalOrderViewOnly,
    }
  )

  const [createAppraisalOrder, { isError, error: appraisalOrderError }] =
    useCreateAppraisalOrderMutation()
  const customerStated = _find(application?.valuations, { type: 'customer-stated' })
  const { data: profiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const solarEscalation = escalations?.find((escalation) => escalation.type === 'roof-solar-panel')

  if (!application || !profiles) return null

  const getAppraisalOrderError = (error) => {
    return error.data?.error || null
  }

  const { address } = application.property
  const { street, county, city, state, zipCode, unitNumber = null } = address

  const applicantProfile = _find(profiles.results, { position: 1 })?.profile

  const { first_name, middle_name = '', last_name, phone = '', email = '' } = applicantProfile

  const solarPathInstructions = {
    'financed-not-paying-off':
      'Hello, please do NOT include solar in valuation as it is financed/leased. In this case, the solar panels are financed; so we will want the appraiser to not give a value to these Solar Panels on the Appraisal. Please appraise as-is.  Ownership, by our definition, is when the solar panels are owned free and clear without any financing related to the equipment. Thank you.',
    'financed-paying-off-as-condition':
      'Hello, please INCLUDE the solar in the valuation. Unlock is going to be paying off the solar lien at closing. Appraiser to mark this checkbox on the traditional appraisal: (subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed.). Appraisers to add this commentary on the addendum on hybrid and traditional appraisal: (subject to hypothetical condition that the loan for the solar panels is paid off and solar panels are owned by the homeowner. Unlock will pay off the lease of the solar panels in funding.). The value for the solar panels can be added on the grid under a separate line item. The value add for the solar needs to be supported by the market/comps (or other approach); analysis for the solar value add needs to be provided on the addendum specifying on how the amount was assigned to it.',
    'owned-free-and-clear':
      'Subject property currently has solar system. Credit and title indicate there are no solar liens. Please INCLUDE solar as part of the real property valuation. The value for the solar panels can be added on the grid under a separate line item. The value add for the solar needs to be supported by the market/comps (or other approach); analysis for the solar value add needs to be provided on the addendum specifying on how the amount was assigned to it.',
  }

  let initialValues
  if (appraisalOrderViewOnly && appraisalOrders?.length) {
    initialValues = appraisalOrders[0]
  } else {
    initialValues = {
      street_name: street,
      county,
      city,
      state,
      unit_number: unitNumber,
      zip: zipCode,
      property_type: 'single-family',
      report_type: '',
      estimated_value: customerStated?.value,
      new_loan_amount: application.requestedInvestment,
      new_loan_number: application.externalId || '',
      loan_officer_first_name: user?.first_name,
      loan_officer_last_name: user?.last_name || '',
      loan_officer_email: user?.username || user?.email || '',
      first_name,
      middle_name,
      last_name,
      email,
      phone,
      verification: false,
      vendor_instructions:
        solarEscalation?.path_solar_panel && solarEscalation?.approved
          ? solarPathInstructions[solarEscalation?.path_solar_panel]
          : '',
    }
  }

  if (appraisalOrderModelOpen === false) return null

  return (
    <div
      id='overlay'
      className='top-0 left-0 fixed w-screen h-screen bg-gray-400/60 z-50 flex items-center justify-center'
    >
      <div className='w-[47%] max-h-[90%] bg-white p-8 mb-4 mr-10 overflow-y-auto'>
        <div className='flex justify-between mb-4'>
          <h2 className='text-sky-400'>Appraisal Report - Order Form</h2>
          <XMarkIcon
            className='cursor-pointer'
            width={30}
            fill='grey'
            onClick={() => dispatch(setAppraisalOrderModalOpen(false))}
          />
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={async (values: any, actions) => {
            setError(false)

            const newValues = {
              ...values,
              unit_number: values.unit_number === '' ? null : values.unit_number,
              application: application?.id,
            }

            delete newValues.verification

            const data: any = await createAppraisalOrder(newValues)

            actions.setSubmitting(false)

            if (!data.error) {
              if (data.data.is_success == false) {
                return setError(true)
              }

              return setOrder(data.data)
            }

            setError(true)
          }}
        >
          {({ values, isSubmitting }) => (
            <LockChild shouldBeLocked={appraisalOrderViewOnly}>
              <Form>
                <h3 className='mb-4'>Property Details</h3>

                <div className='grid grid-cols-4 gap-4 mb-8'>
                  <InputField
                    label='Address'
                    style='default'
                    name='street_name'
                    placeholder='Address'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Unit'
                    style='default'
                    name='unit_number'
                    placeholder='Unit'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='ZIP'
                    style='default'
                    name='zip'
                    placeholder='ZIP'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='City'
                    style='default'
                    name='city'
                    placeholder='City'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='State'
                    style='default'
                    name='state'
                    placeholder='State'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='County'
                    style='default'
                    name='county'
                    placeholder='County'
                    type='text'
                    showError={true}
                  />
                  <SelectField
                    style='default'
                    label='Property Type'
                    name='property_type'
                    options={appraisalOrderPropertyTypeOptions}
                  />
                </div>

                <h3 className='mb-4'>Order Details</h3>

                <div className='grid grid-cols-4 gap-4 mb-8'>
                  <SelectField
                    style='default'
                    label='Report Type'
                    name='report_type'
                    options={appraisalOrderReportTypeOptions}
                  />
                  <NumberField
                    inputStyle='default'
                    label='Estimated Value'
                    name='estimated_value'
                    placeholder='Estimated Value'
                    suffix=''
                    prefix='$'
                    thousandSeparator={true}
                    showError={true}
                  />
                  <NumberField
                    inputStyle='default'
                    label='New Loan Amount'
                    name='new_loan_amount'
                    placeholder='New Loan Amount'
                    suffix=''
                    prefix='$'
                    thousandSeparator={true}
                    showError={true}
                  />
                  <InputField
                    label='New Loan No.'
                    style='default'
                    name='new_loan_number'
                    placeholder='New Loan No.'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Loan Officer First Name'
                    style='default'
                    name='loan_officer_first_name'
                    placeholder='Loan Officer First Name'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Loan Officer Last Name'
                    style='default'
                    name='loan_officer_last_name'
                    placeholder='Loan Officer Last Name'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Loan Officer Email'
                    style='default'
                    name='loan_officer_email'
                    placeholder='Loan Officer Email'
                    type='text'
                    showError={true}
                  />
                </div>

                <h3 className='mb-4'>Customer</h3>

                <div className='grid grid-cols-4 gap-4 mb-4'>
                  <InputField
                    label='First Name'
                    style='default'
                    name='first_name'
                    placeholder='First Name'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Middle Name'
                    style='default'
                    name='middle_name'
                    placeholder='Middle Name'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Last Name'
                    style='default'
                    name='last_name'
                    placeholder='Last Name'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Phone'
                    style='default'
                    name='phone'
                    placeholder='Phone'
                    type='text'
                    showError={true}
                  />
                  <InputField
                    label='Email'
                    style='default'
                    name='email'
                    placeholder='Email'
                    type='text'
                    showError={true}
                  />
                </div>
                <h3 className='mb-4'>Vendor Instructions</h3>

                <InputField
                  style='default'
                  name='vendor_instructions'
                  placeholder='Instructions'
                  as='textarea'
                  rows={4}
                  showError={true}
                />

                {isError && appraisalOrderError && (
                  <div className='mb-4 flex flex-col gap-2 text-error'>
                    <p className='text-center'>{getAppraisalOrderError(appraisalOrderError)}</p>
                  </div>
                )}

                <div className='flex justify-center gap-8'>
                  <div className='flex gap-4 items-center'>
                    {order ? (
                      <div className='flex flex-col gap-2 items-center text-center'>
                        <div className='flex justify-center items-center'>
                          <p>Your Appraisal Report has been submitted</p>
                          <CheckIcon
                            className='ml-5 text-white bg-success rounded'
                            width={20}
                          />
                        </div>
                        <p>
                          Order date {formatDate(new Date(order.created_at), 'dd/MM/yyyy h:mm a')} |
                          Order Number: {order?.['order_number'] || '---'}
                        </p>
                        <div
                          className='flex items-center text-center m-auto'
                          onClick={() => {
                            dispatch(setAppraisalOrderModalOpen(false))
                            setOrder(null)
                          }}
                        >
                          <span className='table m-auto text-blue-500 underline cursor-pointer'>
                            Close this dialogue
                          </span>
                        </div>
                      </div>
                    ) : (
                      !appraisalOrderViewOnly && (
                        <div className='flex flex-col'>
                          <div className='flex gap-4'>
                            <p className='text-right w-[300px]'>
                              I have verified the information on this form and it is ready for
                              submission
                            </p>
                            <Field
                              name='verification'
                              type='checkbox'
                            />
                            <button
                              type='submit'
                              className='px-4 py-2 text-lg bg-sky-400 disabled:bg-sky-300 text-white rounded-md'
                              disabled={!values.verification || isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  Submitting...
                                  <p className='text-xs text-sky-300'>It may take a while...</p>
                                </>
                              ) : (
                                'Order'
                              )}
                            </button>
                          </div>
                          {error && (
                            <p className='text-sm text-error text-center mt-3'>
                              An error ocurred when trying to create the order
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </Form>
            </LockChild>
          )}
        </Formik>
      </div>
    </div>
  )
}
